import React, { useState } from 'react';
import useStepWizard from '../hooks/useStepWizard';
import { useStepActions } from '../hooks/useStepActions';
import WizardLayout from './wizard-layout/WizardLayout';
import PatternSelector from './wizard-steps/PatternSelector';
import PatternConfiguration from './wizard-steps/PatternConfiguration';
import ComponentEndingWizard from './ComponentEndingWizard';
import DurationShapingChoice from './wizard-steps/DurationShapingChoice';
import { useWizardState } from './wizard-navigation/WizardState';
import ShapingWizard from './ShapingWizard';
import IntelliKnitLogger from '../../../shared/utils/ConsoleLogging';
import UnsavedChangesModal from '../../../shared/components/modals/UnsavedChangesModal';
import DurationWizard from './DurationWizard';
// import { useProjectsContext } from '../../projects/hooks/useProjectsContext';
import { useActiveContext } from '../../../shared/hooks/useActiveContext';
import WizardContextBar from './wizard-layout/WizardContextBar';
import PageHeader from '../../../shared/components/PageHeader';
import { calculateFinalStitchCount } from '../../../shared/utils/stitchCalculatorUtils';
import { isAdvancedRowByRowPattern } from '../../../shared/utils/stepDisplayUtils';
import { StandardModal } from '../../../shared/components/modals/StandardModal';
import PrepNoteColorScreen from './wizard-screens/PrepNoteColorScreen';
import ColorSelectionScreen from './wizard-screens/ColorSelectionScreen';
import StripesConfig from './pattern-configs/StripesConfig';
import BriocheConfig from './pattern-configs/BriocheConfig';
import MarledStripesConfig from './pattern-configs/MarledStripesConfig';


const StepWizard = ({ componentIndex, onGoToLanding, editingStepIndex = null, editMode = null, onBack, mode = 'project' }) => {
  const wizard = useStepWizard(componentIndex, editingStepIndex, editMode, mode);
  const { handleAddStep, handleAddStepWithCustomData } = useStepActions(wizard, onBack, mode);
  const { currentProject } = useActiveContext(mode);
  const [showShapingWizard, setShowShapingWizard] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showStepConfirmModal, setShowStepConfirmModal] = useState(false);
  const [pendingShapingInfo, setPendingShapingInfo] = useState(null);
  const [showPrepColorScreen, setShowPrepColorScreen] = useState(true); // Start with prep/color screen

  // ADD THIS with the other state declarations:
  const [showStripesConfig, setShowStripesConfig] = useState(false);
  const [showBriocheConfig, setShowBriocheConfig] = useState(false);
  const [showMarledStripesConfig, setShowMarledStripesConfig] = useState(false);

  const wizardState = useWizardState(wizard, onBack, mode);

  // Add this function inside StepWizard component:
  const handleConfirmIntrinsicStep = () => {
    if (pendingShapingInfo) {

      // Create updated wizard data object synchronously
      const updatedWizardData = {
        ...wizard.wizardData,
        hasShaping: true,
        shapingConfig: createIntrinsicShapingConfig(pendingShapingInfo)
      };

      // Create a temporary wizard object with updated data
      const updatedWizard = {
        ...wizard,
        wizardData: updatedWizardData
      };

      // Call handleAddStep with the updated wizard data
      handleAddStepWithCustomData(updatedWizard);

      // Clean up
      setShowStepConfirmModal(false);
      setPendingShapingInfo(null);
    }
  };

  const handleCancelIntrinsicStep = () => {
    setShowStepConfirmModal(false);
    setPendingShapingInfo(null);
  };

  const [showColorSelectionScreen, setShowColorSelectionScreen] = useState(false);

  const handlePrepColorContinue = (destination) => {
    setShowPrepColorScreen(false);

    if (destination === 'color-selection') {
      setShowColorSelectionScreen(true);
    } else if (destination === 'stripes-config') {
      setShowStripesConfig(true);
    } else if (destination === 'brioche-config') {
      setShowBriocheConfig(true);
    } else if (destination === 'pattern-selection' || destination === 'pattern-override') {
      // Reset wizard to step 1 for pattern selection
      wizard.navigation.goToStep(1);
    } else {
      // Default: go to duration-shaping (step 3)
      wizard.navigation.goToStep(3);
    }
  };

  const handleColorSelectionContinue = (destination) => {
    setShowColorSelectionScreen(false);

    if (destination === 'stripes-config') {
      // ✅ CHANGED: Don't save to stitchPattern - StripesConfig will save to colorwork
      setShowStripesConfig(true); // Show stripes config screen
    } else if (destination === 'marled-stripes-config') {
      setShowMarledStripesConfig(true);
    } else if (destination === 'brioche-config') {
      setShowBriocheConfig(true);
    } else {
      // Color configured, go to pattern selection
      wizard.navigation.goToStep(1);
    }
  };

  const handleStripesConfigContinue = () => {
    setShowStripesConfig(false);
    // After stripes configured, go to pattern selection
    wizard.navigation.goToStep(1);
  };

  const handleMarledStripesConfigContinue = () => {
    setShowMarledStripesConfig(false);
    wizard.navigation.goToStep(1);
  };

  const handleBriocheConfigContinue = () => {
    setShowBriocheConfig(false);
    wizard.navigation.goToStep(4);
  };

  // Component validation
  if (!wizard.component) {
    return (
      <div className="min-h-screen bg-yarn-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-xl p-6 shadow-lg border-2 border-wool-200">
          <div className="text-4xl mb-4">❌</div>
          <h3 className="text-lg font-medium text-wool-600 mb-2">Component not found</h3>
          <button
            onClick={onBack}
            className="btn-primary btn-sm"
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // 🎯 NEW: Simple navigation object - no more calculations!
  const navigation = {
    canProceed: wizard.navigation.canProceed,
    nextStep: wizard.navigation.nextStep,
    previousStep: () => {
      // If at step 1, go back to prep/color screen
      if (wizard.wizardStep === 1) {
        setShowPrepColorScreen(true);
        return;
      }

      const result = wizard.navigation.previousStep();

      if (result?.shouldExit) {
        IntelliKnitLogger.debug('Step Wizard', 'Navigation stack empty - exiting wizard');
        onBack();
      }
    },
    goToStep: wizard.navigation.goToStep
  };

  // Helper to get existing prep note when editing  
  const getExistingPrepNote = () => {
    const stepIndex = editingStepIndex;

    if (stepIndex !== null && wizard.component?.steps?.[stepIndex]) {
      const editingStep = wizard.component.steps[stepIndex];
      return editingStep.prepNote ||
        editingStep.wizardConfig?.prepNote ||
        editingStep.advancedWizardConfig?.prepNote ||
        '';
    }
    return wizard.wizardData.prepNote || '';
  };

  /**
   * Detect if a pattern has intrinsic shaping from row-by-row instructions
   * Put this function inside StepWizard.jsx since it needs both utilities
   */
  const detectIntrinsicShaping = (wizardData, currentStitches) => {
    const { pattern, entryMode, rowInstructions } = wizardData.stitchPattern || {};

    // Only check advanced row-by-row patterns
    if (!isAdvancedRowByRowPattern(pattern) || entryMode !== 'row_by_row') {
      return null;
    }

    // Must have row instructions to analyze
    if (!rowInstructions || rowInstructions.length === 0) {
      return null;
    }

    // Calculate final stitch count using existing utilities
    const finalStitches = calculateFinalStitchCount(rowInstructions, currentStitches);
    // Check if stitches changed
    if (finalStitches !== currentStitches) {
      const netChange = finalStitches - currentStitches;
      return {
        hasIntrinsicShaping: true,
        startingStitches: currentStitches,
        endingStitches: finalStitches,
        netChange,
        action: netChange > 0 ? 'increase' : 'decrease',
        amount: Math.abs(netChange)
      };
    }

    return { hasIntrinsicShaping: false };
  };

  /**
   * Create shaping config for intrinsic pattern shaping
   * Put this function inside StepWizard.jsx as well
   */
  const createIntrinsicShapingConfig = (shapingInfo) => {
    // Get the actual row count from rowInstructions
    const rowCount = wizard.wizardData.stitchPattern?.rowInstructions?.length || 1;

    return {
      type: 'intrinsic_pattern',
      config: {
        action: shapingInfo.action,
        amount: shapingInfo.amount,
        patternBased: true,
        calculation: {
          instruction: "Shaping integrated into pattern rows",
          startingStitches: shapingInfo.startingStitches,
          endingStitches: shapingInfo.endingStitches,
          totalRows: rowCount,
          netStitchChange: shapingInfo.netChange
        }
      }
    };
  };

  // If showing ending wizard
  // Replace the commented-out section with this:
  if (wizardState.showEndingWizard) {
    return (
      <ComponentEndingWizard
        component={wizard.component}
        onBack={wizardState.handleBackFromEnding}
        onComplete={wizardState.handleEndingComplete}
      />
    );
  }

  // If showing shaping wizard
  if (showShapingWizard) {

    return (
      <ShapingWizard
        wizardData={wizard.wizardData}
        mode={mode}
        updateWizardData={wizard.updateWizardData}
        currentStitches={wizard.currentStitches}
        construction={wizard.construction}
        setConstruction={wizard.setConstruction}
        setCurrentStitches={wizard.setCurrentStitches}
        component={wizard.component}
        componentIndex={wizard.componentIndex}
        project={currentProject}
        editingStepIndex={wizard.editingStepIndex}
        onExitToComponentSteps={mode === 'notepad' ? () => { handleAddStep(); } : onBack}
        onBack={() => {
          setShowShapingWizard(false);

          // Check if shaping was actually completed
          const hasCompletedShaping = wizard.wizardData.shapingConfig?.type &&
            wizard.wizardData.shapingConfig?.config?.calculation;

          console.log("hasCompletedShaping result:", hasCompletedShaping);
          console.log("========================");


          IntelliKnitLogger.debug('ShapingWizard onBack', {
            hasCompletedShaping,
            shapingConfigType: wizard.wizardData.shapingConfig?.type,
            hasCalculation: !!wizard.wizardData.shapingConfig?.config?.calculation
          });

          if (hasCompletedShaping) {
            // Shaping completed - advance to next step
            wizard.navigation.nextStep();
          } else {
            // Shaping not completed - clear selection and stay
            wizard.updateWizardData('hasShaping', false);
            wizard.updateWizardData('choiceMade', false);
          }
        }}
        onGoToLanding={onGoToLanding} // Just added
      />
    );
  }

  // Check for unsaved data
  const hasUnsavedData = () => {
    const { wizardStep, wizardData } = wizard;

    switch (wizardStep) {
      case 1: // Pattern Selection
        return wizardData.stitchPattern?.pattern || wizardData.stitchPattern?.category;
      case 2: // Pattern Configuration
        return true; // Always has pattern data from step 1
      case 3: // Duration/Shaping Choice
        return wizardData.hasShaping !== undefined || wizardData.choiceMade;
      case 4: // Duration Config
        return true; // Always show warning - user is in step creation flow
      default:
        return false;
    }
  };

  const handleXButtonClick = () => {
    if (hasUnsavedData()) {
      setShowExitModal(true);
    } else {
      onBack();
    }
  };

  const handleConfirmExit = () => {
    setShowExitModal(false);
    setShowPrepColorScreen(true); // Reset to start
    wizard.resetWizardData();
    onBack();
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
  };

  // Then render normal wizard steps
  const renderCurrentStep = () => {
    switch (wizard.wizardStep) {
      case 1:
        return (
          <div className="stack-lg">
            <PatternSelector
              wizardData={wizard.wizardData}
              updateWizardData={wizard.updateWizardData}
              navigation={navigation}
              existingPrepNote={getExistingPrepNote()}
              onSavePrepNote={(note) => wizard.updateWizardData('prepNote', note)}
            />

            {/* 🎯 SIMPLIFIED: Navigation buttons for Step 1 */}
            <div className="pt-6 border-t border-wool-100">
              <div className="flex gap-3">
                <button
                  onClick={navigation.previousStep}
                  className="flex-1 btn-tertiary"
                >
                  ← Cancel
                </button>

                <button
                  onClick={navigation.nextStep}
                  disabled={!navigation.canProceed()}
                  className="flex-2 btn-primary"
                  style={{ flexGrow: 2 }}
                >
                  Continue →
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="stack-lg">
            <PatternConfiguration
              wizardData={wizard.wizardData}
              updateWizardData={wizard.updateWizardData}
              navigation={navigation}
              construction={wizard.construction}
              existingPrepNote={getExistingPrepNote()}
              onSavePrepNote={(note) => wizard.updateWizardData('prepNote', note)}
              currentStitches={wizard.currentStitches}
              project={currentProject}
              mode={mode}
              startingSide={
                // ✅ FIX: Check for user override FIRST
                wizard.wizardData.sideTracking?.startingSide ||
                wizard.getStepStartingSide()
              }
            />

            {/* 🎯 SIMPLIFIED: Navigation for Step 2 */}
            <div className="pt-6 border-t border-wool-100">
              <div className="flex gap-3">
                <button
                  onClick={navigation.previousStep}
                  className="flex-1 btn-tertiary"
                >
                  ← Back
                </button>

                <button
                  onClick={() => {
                    const { pattern } = wizard.wizardData.stitchPattern || {};

                    if (pattern === 'Custom') {
                      wizard.navigation.goToStep(4);
                    } else {
                      wizard.navigation.nextStep();
                    }
                  }}

                  disabled={!navigation.canProceed()}
                  className="flex-2 btn-primary"
                  style={{ flexGrow: 2 }}
                >
                  {wizard.wizardData.stitchPattern.pattern === 'Cast On' ? 'Add Step' : 'Continue →'}
                </button>
              </div>
            </div>
          </div>
        );

      case 3:
        // Duration/Shaping choice - auto-advances, no nav buttons needed
        return (
          <DurationShapingChoice
            wizardData={wizard.wizardData}
            updateWizardData={wizard.updateWizardData}
            construction={wizard.construction}
            onAdvanceStep={() => wizard.navigation.goToStep(4)}
            onShowShapingWizard={() => setShowShapingWizard(true)}
            existingPrepNote={wizard.wizardData.prepNote || ''}
            onSavePrepNote={(note) => wizard.updateWizardData('prepNote', note)}
          />
        );

      case 4:
        // Duration Choice - needs nav buttons
        return (
          <DurationWizard
            wizardData={wizard.wizardData}
            updateWizardData={wizard.updateWizardData}
            currentStitches={wizard.currentStitches}
            construction={wizard.construction}
            componentIndex={wizard.componentIndex}
            editingStepIndex={wizard.editingStepIndex}
            project={currentProject}
            onBack={navigation.previousStep} // 🎯 SIMPLIFIED: Direct navigation call
            onExitToComponentSteps={mode === 'notepad' ? () => { handleAddStep(); } : onBack}
            mode={mode}
          />
        );

      default:
        return <div>Step not found</div>;
    }
  };

  return (
    <WizardLayout>
      <PageHeader
        useBranding={true}
        onHome={onGoToLanding}
        onBack={
          showPrepColorScreen ? onBack :
            showBriocheConfig ? () => {
              setShowBriocheConfig(false);
              wizard.updateWizardData('colorwork', {
                letters: wizard.wizardData.colorwork?.letters || [],
                type: null,
              });
              setShowColorSelectionScreen(true);
            } :
              navigation.previousStep
        }
        showCancelButton={true}
        onCancel={handleXButtonClick}
        compact={true}
        sticky={true}
      />
      <WizardContextBar wizard={wizard} />
      <div className="p-6 bg-yarn-50 min-h-screen">
        {showPrepColorScreen ? (
          <PrepNoteColorScreen
            wizardData={wizard.wizardData}
            updateWizardData={wizard.updateWizardData}
            component={wizard.component}
            onContinue={handlePrepColorContinue}
            onBack={onBack}
            onCancel={handleXButtonClick}
            onFinishComponent={() => {
              wizardState.handleFinishComponent();  // ✅ This is the right function name
            }}
          />
        ) : showColorSelectionScreen ? (
          <ColorSelectionScreen
            wizardData={wizard.wizardData}
            updateWizardData={wizard.updateWizardData}
            component={wizard.component}
            onContinue={handleColorSelectionContinue}
            onBack={() => setShowPrepColorScreen(true)}
            onCancel={handleXButtonClick}
          />
        ) : showStripesConfig ? (
          <>
            <StripesConfig
              wizardData={wizard.wizardData}
              updateWizardData={wizard.updateWizardData}
              construction={wizard.construction}
              project={currentProject}
              mode="create"
            />

            {/* Navigation Buttons */}
            <div className="pt-6 border-t border-wool-100">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowStripesConfig(false);
                    setShowColorSelectionScreen(true);
                  }}
                  className="flex-1 btn-tertiary"
                >
                  ← Back
                </button>
                <button
                  onClick={handleStripesConfigContinue}
                  disabled={!wizard.wizardData.colorwork?.stripeSequence?.length}
                  className="flex-2 btn-primary"
                  style={{ flexGrow: 2 }}
                >
                  Continue →
                </button>
              </div>
            </div>
          </>
        ) : showMarledStripesConfig ? (
          <>
            <MarledStripesConfig
              wizardData={wizard.wizardData}
              updateWizardData={wizard.updateWizardData}
              construction={wizard.construction}
              project={currentProject}
              mode="create"
            />
            <div className="pt-6 border-t border-wool-100">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowMarledStripesConfig(false);
                    setShowColorSelectionScreen(true);
                  }}
                  className="flex-1 btn-tertiary"
                >
                  ← Back
                </button>
                <button
                  onClick={handleMarledStripesConfigContinue}
                  disabled={!wizard.wizardData.colorwork?.marledSequence?.length}
                  className="flex-2 btn-primary"
                  style={{ flexGrow: 2 }}
                >
                  Continue →
                </button>
              </div>
            </div>
          </>
        ) : showBriocheConfig ? (
          <>
            <BriocheConfig
              wizardData={wizard.wizardData}
              updateWizardData={wizard.updateWizardData}
              construction={wizard.construction}
              currentStitches={wizard.currentStitches}
              mode="create"
            />
            {/* Navigation Buttons */}
            <div className="pt-6 border-t border-wool-100">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowBriocheConfig(false);
                    // ✅ THIS CODE clears brioche data
                    wizard.updateWizardData('colorwork', {  // Note: wizard.updateWizardData
                      letters: wizard.wizardData.colorwork?.letters || [],
                      type: null,
                    });
                    setShowColorSelectionScreen(true);
                  }}
                  className="flex-1 btn-tertiary"
                >
                  ← Back
                </button>
                <button
                  onClick={handleBriocheConfigContinue}
                  disabled={false}
                  className="flex-2 btn-primary"
                  style={{ flexGrow: 2 }}
                >
                  Continue →
                </button>
              </div>
            </div>
          </>
        ) : (
          renderCurrentStep()
        )}

      </div>

      <UnsavedChangesModal
        isOpen={showExitModal}
        onConfirmExit={handleConfirmExit}
        onCancel={handleCancelExit}
      />

      {/* Step Confirmation Modal for Intrinsic Shaping */}
      <StandardModal
        isOpen={showStepConfirmModal}
        onClose={handleCancelIntrinsicStep}
        onConfirm={handleConfirmIntrinsicStep}
        category="simple"
        colorScheme="sage"
        title="Ready to Add Step"
        subtitle="Shaped pattern detected"
        icon="🧶"
        primaryButtonText="Add Step"
        secondaryButtonText="Cancel"
      >
        {pendingShapingInfo && (
          <div className="space-y-4">
            <div className="bg-sage-50 border border-sage-200 rounded-lg p-4">
              <h4 className="font-medium text-sage-800 mb-2">Pattern Summary</h4>
              <div className="space-y-2 text-sm text-sage-700">
                <div className="flex justify-between">
                  <span>Pattern Type:</span>
                  <span className="font-medium">{wizard.wizardData.stitchPattern.pattern}</span>
                </div>
                <div className="flex justify-between">
                  <span>Entry Mode:</span>
                  <span className="font-medium">Row-by-Row</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Rows:</span>
                  <span className="font-medium">{wizard.wizardData.stitchPattern.rowInstructions?.length || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-lavender-50 border border-lavender-200 rounded-lg p-4">
              <h4 className="font-medium text-lavender-800 mb-2">Shaping Detected</h4>
              <div className="space-y-2 text-sm text-lavender-700">
                <div className="flex justify-between">
                  <span>Starting Stitches:</span>
                  <span className="font-medium">{pendingShapingInfo.startingStitches}</span>
                </div>
                <div className="flex justify-between">
                  <span>Ending Stitches:</span>
                  <span className="font-medium">{pendingShapingInfo.endingStitches}</span>
                </div>
                <div className="flex justify-between">
                  <span>Net Change:</span>
                  <span className={`font-medium ${pendingShapingInfo.netChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {pendingShapingInfo.netChange > 0 ? '+' : ''}{pendingShapingInfo.netChange} stitches
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Shaping Type:</span>
                  <span className="font-medium capitalize">{pendingShapingInfo.action}</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-wool-600">
              Your pattern includes built-in shaping. This step will be added with the shaping automatically configured.
            </p>
          </div>
        )}
      </StandardModal>

    </WizardLayout>
  );
};

export default StepWizard;